import {
  call,
  cancel,
  fork,
  put,
  select,
  takeEvery,
} from 'typed-redux-saga/macro';
import type { Task } from 'redux-saga';

import { sagaIntl } from '@i18n';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { isBlankFilename } from '@helpers/draftFilename';
import { TDOId } from '@common-modules/universal/models/Brands';
import {
  RENAME_DOCUMENT_FAILURE,
  RENAME_DOCUMENT_REQUEST,
  RENAME_DOCUMENT_SUCCESS,
} from '../actions';
import { serviceUpdateTdoName } from '../services';
import { selectToken, selectGraphQLConfig } from './auth';

export function* onRenameDocumentRequest({
  payload: { tdoId, name, previousName },
}: ReturnType<typeof RENAME_DOCUMENT_REQUEST>) {
  try {
    // The header refuses these too; refused here as well so no other caller
    // can store an extension-only name. Thrown so the optimistic name rolls
    // back like any other failure.
    if (isBlankFilename(name)) {
      throw new Error('Refused a blank document name');
    }
    const token: string = yield* select(selectToken);
    const cfg = yield* select(selectGraphQLConfig);
    // Service returns what the TDO now holds — the server-formatted name
    // (leading whitespace trimmed, etc.) and the date the rename bumped — so
    // the slice and the rows end up matching it.
    const persisted = yield* call(
      serviceUpdateTdoName,
      tdoId,
      name,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    yield* put(RENAME_DOCUMENT_SUCCESS({ tdoId, ...persisted }));
  } catch (e) {
    const message =
      e instanceof Error ? e.message : 'Failed to rename document';
    console.warn('[documents] rename failed; rolling back.', e);
    // The rollback alone looks like the rename never happened (VE-32151).
    yield* put(
      enqueueSnackbar({
        message: sagaIntl().formatMessage({
          id: 'documentRenameFailed',
          defaultMessage: 'Could not rename the document.',
        }),
        variant: 'error',
      })
    );
    yield* put(RENAME_DOCUMENT_FAILURE({ tdoId, previousName, message }));
  }
}

/**
 * One rename in flight PER DOCUMENT (VE-31012).
 *
 * `takeLatest` superseded on the action type alone, so renaming a second
 * document while the first was still saving cancelled the first — and by then
 * the write had usually gone out, leaving the server holding the new name
 * while the rows and the parked session kept the old one, with no FAILURE to
 * roll anything back. Keyed on the tdoId the request names, a second rename of
 * the SAME document still supersedes the first, which is what `takeLatest` was
 * there for.
 */
export function* renameSaga() {
  const inFlight = new Map<TDOId, Task>();
  yield* takeEvery(RENAME_DOCUMENT_REQUEST, function* (action) {
    const { tdoId } = action.payload;
    const superseded = inFlight.get(tdoId);
    if (superseded) {
      yield* cancel(superseded);
    }
    // Forget the renames that have already settled, so the map holds what is
    // in flight rather than every document renamed this session.
    inFlight.forEach((task, id) => {
      if (!task.isRunning()) {
        inFlight.delete(id);
      }
    });
    inFlight.set(tdoId, yield* fork(onRenameDocumentRequest, action));
  });
}
